import React, { useState, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import formatDate from "../../utils/formatDate";
import {
  getPaymentMethod,
  getVerificationStatus,
  extractAccessCode,
  getSessionType,
} from "./utils";
import { PaymentMethodBadge } from "./SessionBadges";
import { getConfigWithToken } from "../../config/config";

const PAYMENT_METHOD_OPTIONS = [
  { value: "insurance", label: "Insurance" },
  { value: "self-pay", label: "Self-Pay" },
  { value: "access code", label: "Access Code" },
];

const normalizePaymentMethodValue = (value) => {
  const lower = String(value || "").toLowerCase().trim();
  if (lower === "cash" || lower === "self-pay") return "self-pay";
  if (lower === "access code") return "access code";
  if (lower === "insurance") return "insurance";
  return lower;
};

const paymentMethodLabel = (value) => {
  const normalized = normalizePaymentMethodValue(value);
  const match = PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === normalized);
  return match?.label || value || "N/A";
};

const resolveInsurancePlanName = (paymentData, session, insuranceDetails) =>
  paymentData?.insurancePayer ||
  paymentData?.planDetails ||
  paymentData?.insurancePlan ||
  paymentData?.planName ||
  insuranceDetails?.insurancePlan ||
  insuranceDetails?.planName ||
  insuranceDetails?.payerName ||
  session?.insurance?.planName ||
  session?.insurance?.insurancePlan ||
  session?.insurance?.payerName ||
  session?.user?.insurance?.planName ||
  session?.user?.insurance?.insurancePlan ||
  session?.user?.insurance?.payerName ||
  session?.user?.insurancePlan ||
  session?.user?.insurancePayer ||
  session?.planName ||
  null;

const resolveInsuranceMemberId = (paymentData, session, insuranceDetails) =>
  paymentData?.memberId ||
  paymentData?.insuranceMemberId ||
  insuranceDetails?.memberId ||
  insuranceDetails?.insuranceMemberId ||
  session?.insurance?.memberId ||
  session?.user?.insurance?.memberId ||
  session?.user?.memberId ||
  session?.memberId ||
  null;

const SessionDetailsDrawer = ({
  showDetailsDrawer,
  selectedSession,
  setShowDetailsDrawer,
  setSelectedSession,
  paymentDataBySession,
  getActualStatus,
  sessionHistoryDetailsBySession,
  showSessionStatusSection = false,
  onVerifyInsurance,
  insuranceActionCount = 0,
  onPaymentMethodChanged,
}) => {
  const [sessionNotes, setSessionNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showChangePaymentModal, setShowChangePaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [changingPaymentMethod, setChangingPaymentMethod] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState(null);

  const user = useSelector((state) => state?.user);

  const sessionId = selectedSession?._id || selectedSession?.id;

  const fetchSessionNotes = async () => {
    if (!sessionId) return;

    try {
      setLoadingNotes(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/${sessionId}/notes`,
        getConfigWithToken(user.user?.token),
      );

      if (response.data.success) {
        const notesData = response.data.data;
        const notesArray = Array.isArray(notesData) ? notesData : [];
        setSessionNotes(notesArray);
      }
    } catch (error) {
      console.error("Error fetching session notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (showDetailsDrawer && sessionId) {
      fetchSessionNotes();
    }
  }, [showDetailsDrawer, sessionId, insuranceActionCount]);

  useEffect(() => {
    if (!showDetailsDrawer || !sessionId) {
      setInsuranceDetails(null);
      return;
    }

    const method = getPaymentMethod(
      selectedSession,
      paymentDataBySession,
    )?.toLowerCase();

    if (method !== "insurance") {
      setInsuranceDetails(null);
      return;
    }

    let cancelled = false;

    const fetchInsuranceDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/payments/insurance-details/${sessionId}`,
          getConfigWithToken(user.user?.token),
        );

        if (!cancelled && response.data.success) {
          setInsuranceDetails(response.data.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching insurance details:", error);
          setInsuranceDetails(null);
        }
      }
    };

    fetchInsuranceDetails();

    return () => {
      cancelled = true;
    };
    // Only refetch when the drawer session or insurance verification changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailsDrawer, sessionId, insuranceActionCount, user.user?.token]);

  const handleSaveNote = async () => {
    if (!sessionId) return;

    if (!noteText || noteText.trim() === "") {
      toast.error("Please enter a note");
      return;
    }

    try {
      setSavingNote(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/appointment/${sessionId}/notes`,
        { noteText: noteText.trim() },
        getConfigWithToken(user.user?.token),
      );

      if (response.data.success) {
        toast.success("Note saved successfully");
        setNoteText("");
        await fetchSessionNotes();
      } else {
        toast.error(response.data.message || "Failed to save note");
      }
    } catch (error) {
      console.error("Error saving note:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Super Admin only.");
      } else {
        toast.error(error?.response?.data?.message || "Failed to save note");
      }
    } finally {
      setSavingNote(false);
    }
  };

  const openChangePaymentModal = () => {
    const current = normalizePaymentMethodValue(
      getPaymentMethod(selectedSession, paymentDataBySession),
    );
    setSelectedPaymentMethod(current || "insurance");
    setShowChangePaymentModal(true);
  };

  const handleChangePaymentMethod = async () => {
    if (!sessionId) return;
    if (!selectedPaymentMethod) {
      toast.error("Select a valid payment method.");
      return;
    }

    const current = normalizePaymentMethodValue(
      getPaymentMethod(selectedSession, paymentDataBySession),
    );
    if (selectedPaymentMethod === current) {
      setShowChangePaymentModal(false);
      return;
    }

    try {
      setChangingPaymentMethod(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/payments/change-payment-method/${sessionId}`,
        { paymentMethod: selectedPaymentMethod },
        getConfigWithToken(user.user?.token),
      );

      if (response.data.success) {
        toast.success("Payment method updated successfully.");
        setShowChangePaymentModal(false);
        onPaymentMethodChanged?.(response.data.data);
        await fetchSessionNotes();
      } else {
        toast.error(
          response.data.message || "Unable to change payment method.",
        );
      }
    } catch (error) {
      console.error("Error changing payment method:", error);
      toast.error(
        error?.response?.data?.message ||
          "Unable to change payment method. Please try again.",
      );
    } finally {
      setChangingPaymentMethod(false);
    }
  };

  if (!showDetailsDrawer || !selectedSession) return null;

  const paymentData = paymentDataBySession[sessionId];
  const paymentMethod = getPaymentMethod(
    selectedSession,
    paymentDataBySession,
  )?.toLowerCase();
  const verificationStatus = getVerificationStatus(
    selectedSession,
    paymentDataBySession,
  );
  const insurancePlanName = resolveInsurancePlanName(
    paymentData,
    selectedSession,
    insuranceDetails,
  );
  const insuranceMemberId = resolveInsuranceMemberId(
    paymentData,
    selectedSession,
    insuranceDetails,
  );

  return (
    <>
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-end",
        zIndex: 10001,
        padding: 0,
      }}
      onClick={() => {
        setShowDetailsDrawer(false);
        setSelectedSession(null);
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0",
          width: "550px",
          maxWidth: "90vw",
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          margin: 0,
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #e5e5e5",
            position: "sticky",
            top: 0,
            backgroundColor: "#ffffff",
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#000000",
              margin: 0,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Session ID:{" "}
            {selectedSession?.appointment_id ||
              selectedSession?._id?.slice(-4) ||
              "N/A"}
          </h2>
          <button
            onClick={() => {
              setShowDetailsDrawer(false);
              setSelectedSession(null);
            }}
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #E0E0E0",
              borderRadius: "6px",
              fontSize: "14px",
              cursor: "pointer",
              color: "#000000",
              padding: "8px 16px",
              fontWeight: "400",
              fontFamily: "sans-serif",
              outline: "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#F5F5F5";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#ffffff";
            }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "24px",
              flexWrap: "nowrap",
              width: "100%",
            }}
          >
            {getPaymentMethod(selectedSession, paymentDataBySession) &&
              getPaymentMethod(selectedSession, paymentDataBySession) !==
                "-" && (
                <div
                  style={{
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  <PaymentMethodBadge
                    session={selectedSession}
                    paymentDataBySession={paymentDataBySession}
                  />
                </div>
              )}

            <div
              style={{
                backgroundColor: "#F3F3F4",
                border: "1px solid #E5E7EB",
                borderRadius: "50px",
                padding: "0px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                color: "#064260",
                fontWeight: "500",
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                lineHeight: "1.2",
              }}
            >
              {selectedSession.appointmenttime
                ? `${formatDate(selectedSession.appointmenttime, "date")} • ${formatDate(selectedSession.appointmenttime, "time")}-${formatDate(selectedSession.appointmentEndTime, "time")}`
                : "N/A"}
            </div>
            <div
              style={{
                backgroundColor: "#F3F3F4",
                border: "1px solid #E5E7EB",
                borderRadius: "50px",
                padding: "6px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                color: "#064260",
                fontWeight: "500",
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                lineHeight: "1.2",
              }}
            >
              {getSessionType(selectedSession)}
            </div>
          </div>

          <div
            style={{
              marginBottom: "24px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              <div style={{ flex: "1", minWidth: "200px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "400",
                    marginBottom: "4px",
                    display: "block",
                    color: "#6B7280",
                  }}
                >
                  Patient
                </label>
                <p
                  style={{
                    margin: "4px 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4B5563",
                  }}
                >
                  {selectedSession?.user?.firstname}{" "}
                  {selectedSession?.user?.lastname}
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "12px",
                    color: "#6B7280",
                    minHeight: "20px",
                  }}
                >
                  DOB:{" "}
                  {selectedSession?.user?.dob
                    ? formatDate(selectedSession?.user?.dob, "date")
                    : "N/A"}
                </p>
                {getPaymentMethod(selectedSession, paymentDataBySession) &&
                  getPaymentMethod(selectedSession, paymentDataBySession) !==
                    "-" && (
                      <div style={{ marginTop: "16px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "400",
                            marginBottom: "4px",
                            display: "block",
                            color: "#6B7280",
                          }}
                        >
                          Payment Method
                        </label>
                        <p
                          style={{
                            margin: "4px 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#4B5563",
                          }}
                        >
                          {paymentMethodLabel(
                            getPaymentMethod(
                              selectedSession,
                              paymentDataBySession,
                            ),
                          )}
                        </p>
                        <button
                          type="button"
                          onClick={openChangePaymentModal}
                          style={{
                            border: "none",
                            background: "none",
                            padding: 0,
                            color: "#0D9488",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Change
                        </button>
                      </div>
                    )}
              </div>

              <div style={{ flex: "1", minWidth: "200px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "400",
                    marginBottom: "4px",
                    display: "block",
                    color: "#6B7280",
                  }}
                >
                  Provider
                </label>
                <p
                  style={{
                    margin: "4px 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4B5563",
                  }}
                >
                  {selectedSession?.health_specialist?.firstname}{" "}
                  {selectedSession?.health_specialist?.lastname}
                </p>
                <div style={{ minHeight: "20px" }}></div>
                {paymentMethod === "insurance" && (
                  <div style={{ marginTop: "16px" }}>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "400",
                        marginBottom: "4px",
                        display: "block",
                        color: "#6B7280",
                      }}
                    >
                      Plan / Member ID
                    </label>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#4B5563",
                      }}
                    >
                      {insurancePlanName || "N/A"}
                    </p>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "12px",
                        color: "#6B7280",
                      }}
                    >
                      ID: {insuranceMemberId || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {showSessionStatusSection &&
            getActualStatus &&
            (() => {
              const actualStatus = getActualStatus(selectedSession);
              const sessionId = selectedSession?._id || selectedSession?.id;
              const sessionDetail = sessionHistoryDetailsBySession?.[sessionId];

              const statusColors = {
                Completed: { bg: "#D0FAE5", color: "#006145" },
                Pending: { bg: "#FFFED8", color: "#687000" },
                "Late Cancel": { bg: "#FEE9C6", color: "#973B00" },
                "No Show": { bg: "#FEE4E6", color: "#ED3A3A" },
                "User Cancel": { bg: "#EDE9FE", color: "#5D0EC1" },
                "Provider Cancel": { bg: "#C6E0FE", color: "#002697" },
                "Admin Cancel": { bg: "#000000", color: "#FFFFFF" },
              };
              const colors = statusColors[actualStatus] || {
                bg: "#FFFED8",
                color: "#687000",
              };

              const isNoShow = actualStatus === "No Show";
              const isLateCancel = actualStatus === "Late Cancel";
              const hasNoShowFee = selectedSession?.no_show_fee === true;
              const hasLateCancelFee =
                selectedSession?.late_cancel_fee === true;
              const isWaived = selectedSession?.waive_fee === true;

              let feeStatusDisplay = null;
              if (!isNoShow && !isLateCancel) {
                feeStatusDisplay = (
                  <span style={{ fontSize: "12px", color: "#6B7280" }}>-</span>
                );
              } else if (isWaived) {
                feeStatusDisplay = (
                  <span
                    style={{
                      backgroundColor: "#EDE9FE",
                      color: "#5D0EC1",
                      fontWeight: "500",
                      padding: "6px 12px",
                      fontSize: "12px",
                      borderRadius: "50px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: "1",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    Waived
                  </span>
                );
              } else if (hasNoShowFee || hasLateCancelFee) {
                feeStatusDisplay = (
                  <span
                    style={{
                      backgroundColor: "#D0FAE5",
                      color: "#006145",
                      fontWeight: "500",
                      padding: "6px 12px",
                      fontSize: "12px",
                      borderRadius: "50px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: "1",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    Applied
                  </span>
                );
              } else {
                feeStatusDisplay = (
                  <span
                    style={{
                      backgroundColor: "#FFFED8",
                      color: "#687000",
                      fontWeight: "500",
                      padding: "6px 12px",
                      fontSize: "12px",
                      borderRadius: "50px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: "1",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    Pending
                  </span>
                );
              }

              return (
                <div
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#FFFFFF",
                    border: "1px dashed #E5E7EB",
                    borderRadius: "8px",
                    padding: "16px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                  >
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          marginBottom: "4px",
                          display: "block",
                          color: "#6B7280",
                        }}
                      >
                        Session Status
                      </label>
                      <span
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.color,
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "inline-block",
                          marginTop: "4px",
                        }}
                      >
                        {actualStatus}
                      </span>
                    </div>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "400",
                          marginBottom: "4px",
                          display: "block",
                          color: "#6B7280",
                        }}
                      >
                        Cancellation Fee Status
                      </label>
                      {feeStatusDisplay}
                    </div>
                  </div>
                </div>
              );
            })()}

          {(paymentMethod === "self-pay" || paymentMethod === "cash") &&
            (() => {
              const isInvalid =
                verificationStatus === "Inactive" ||
                verificationStatus === "Invalid";

              const isFromSessionHistoryAdmin = Boolean(
                showSessionStatusSection,
              );
              let borderColor = "none";
              if (isInvalid) {
                borderColor = "1px solid #EF4444";
              } else if (isFromSessionHistoryAdmin) {
                borderColor = "1px solid #15803D";
              }

              let statusColor = "#15803D";
              if (isInvalid) {
                statusColor = "#EF4444";
              } else if (isFromSessionHistoryAdmin) {
                statusColor = "#15803D";
              }

              let labelColor = "#15803D";
              if (isInvalid) {
                labelColor = "#991B1B";
              } else if (isFromSessionHistoryAdmin) {
                labelColor = "#15803D";
              }

              let amountColor = "#15803D";
              if (isInvalid) {
                amountColor = "#991B1B";
              } else if (isFromSessionHistoryAdmin) {
                amountColor = "#15803D";
              }

              let cardColor = "#15803D";
              if (isInvalid) {
                cardColor = "#991B1B";
              } else if (isFromSessionHistoryAdmin) {
                cardColor = "#15803D";
              }

              let expColor = "#15803D";
              if (isInvalid) {
                expColor = "#991B1B";
              } else if (isFromSessionHistoryAdmin) {
                expColor = "#15803D";
              }
              const amountWeight =
                (isFromSessionHistoryAdmin && !isInvalid) ||
                (!isFromSessionHistoryAdmin && !isInvalid)
                  ? "600"
                  : "400";

              return (
                <div
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    padding: "16px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1F2937",
                      margin: "0 0 16px 0",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Self-Pay
                  </h4>
                  <div
                    style={{
                      backgroundColor: isInvalid ? "#FEE2E2" : "#F0FDF4",
                      border: borderColor,
                      padding: "16px",
                      borderRadius: "6px",
                      position: "relative",
                      minHeight: "120px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        left: "16px",
                        color: statusColor,
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {verificationStatus}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "24px",
                        flexWrap: "nowrap",
                        marginTop: "8px",
                      }}
                    >
                      <div
                        style={{
                          flex: "1",
                          minWidth: "200px",
                          paddingTop: "24px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: labelColor,
                          }}
                        >
                          Self-Pay Total
                        </label>
                        <p
                          style={{
                            margin: "4px 0",
                            fontSize: "14px",
                            color: amountColor,
                            fontWeight: amountWeight,
                          }}
                        >
                          ${paymentData?.amount || "120"}
                        </p>
                        {!isInvalid && (
                          <p
                            style={{
                              margin: "16px 0 0 0",
                              fontSize: "12px",
                              color: "#6B7280",
                              fontWeight: "400",
                            }}
                          >
                            Card on file and preauthorized.
                          </p>
                        )}
                      </div>

                      <div
                        style={{
                          flex: "1",
                          minWidth: "200px",
                          paddingTop: "24px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: labelColor,
                          }}
                        >
                          Card on File
                        </label>
                        <p
                          style={{
                            margin: "4px 0",
                            fontSize: "14px",
                            color: cardColor,
                            fontWeight: "400",
                          }}
                        >
                          {paymentData?.cardLast4
                            ? `Visa • *${paymentData.cardLast4}`
                            : "N/A"}
                        </p>
                        {paymentData?.cardExpYear &&
                          paymentData?.cardExpMonth && (
                            <p
                              style={{
                                margin: "4px 0",
                                fontSize: "12px",
                                color: expColor,
                                fontWeight: "400",
                              }}
                            >
                              Exp:{" "}
                              {String(paymentData.cardExpMonth).padStart(
                                2,
                                "0",
                              )}
                              /{String(paymentData.cardExpYear).slice(-2)}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          {paymentMethod === "access code" &&
            (() => {
              const isInvalid =
                verificationStatus === "Inactive" ||
                verificationStatus === "Invalid";
              return (
                <div
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    padding: "16px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000000",
                      margin: "0 0 16px 0",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Access Code
                  </h4>
                  <div
                    style={{
                      backgroundColor:
                        verificationStatus === "Verified"
                          ? "#F3E8FF"
                          : isInvalid
                            ? "#FEE2E2"
                            : "#FFFFFF", // Light purple when Verified, very light red (almost pink) when Invalid
                      border:
                        verificationStatus === "Verified"
                          ? "1px solid #7C3AED"
                          : isInvalid
                            ? "1px solid #EF4444"
                            : "1px solid #A78BFA", // Dark purple border when Verified, red border when Invalid
                      padding: "16px",
                      borderRadius: "6px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#000000",
                          fontWeight: "400",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "400",
                            color: isInvalid ? "#EF4444" : "#7C3AED",
                          }}
                        >
                          Code:
                        </span>{" "}
                        <span
                          style={{
                            color: isInvalid ? "#EF4444" : "#7C3AED",
                            fontWeight: "400",
                          }}
                        >
                          {extractAccessCode(paymentData?.accessCode) ||
                            extractAccessCode(selectedSession?.accessCode) ||
                            extractAccessCode(
                              selectedSession?.paymentData?.accessCode,
                            ) ||
                            "N/A"}
                        </span>
                      </p>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#000000",
                          fontWeight: "400",
                        }}
                      >
                        <span style={{ fontWeight: "400", color: "#000000" }}>
                          Business:
                        </span>{" "}
                        <span style={{ fontWeight: "600", color: "#000000" }}>
                          {paymentData?.businessName ||
                            selectedSession?.business?.businessName ||
                            selectedSession?.businessName ||
                            "N/A"}
                        </span>
                      </p>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#000000",
                          fontWeight: "400",
                        }}
                      >
                        <span style={{ fontWeight: "400", color: "#000000" }}>
                          Status:
                        </span>{" "}
                        <span
                          style={{
                            color: isInvalid
                              ? "#000000"
                              : verificationStatus === "Verified"
                                ? "#000000"
                                : "#000000",
                            fontWeight: "600",
                          }}
                        >
                          {isInvalid
                            ? "Inactive"
                            : verificationStatus === "Verified"
                              ? "Active"
                              : verificationStatus}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#000000",
                          fontWeight: "400",
                        }}
                      >
                        <span style={{ fontWeight: "400", color: "#000000" }}>
                          Sessions Available:
                        </span>{" "}
                        <span
                          style={{
                            color: "#000000",
                            fontWeight: isInvalid ? "600" : "400",
                          }}
                        >
                          {(() => {
                            if (
                              paymentData?.sessionsAvailable !== null &&
                              paymentData?.sessionsAvailable !== undefined
                            ) {
                              const remaining =
                                paymentData.sessionsAvailable -
                                (paymentData.sessionsUsed || 0);
                              return remaining >= 0 ? remaining : 0; // avoid negative values
                            }
                            return (
                              paymentData?.remainingSessions ||
                              selectedSession?.sessionsAvailable ||
                              "0"
                            );
                          })()}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

          {paymentMethod === "insurance" && (
            <div
              style={{
                marginBottom: "24px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "16px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#064260",
                      margin: 0,
                    }}
                  >
                    Insurance Verification
                  </h4>
                  {verificationStatus === "Pending" && onVerifyInsurance && (
                    <Button
                      onClick={() => onVerifyInsurance(selectedSession)}
                      style={{
                        backgroundColor: "#064260",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: 600,
                        padding: "8px 16px",
                        borderRadius: "8px",
                      }}
                    >
                      Verify Insurance
                    </Button>
                  )}
                  {(verificationStatus === "Verified" ||
                    verificationStatus === "Invalid") &&
                    onVerifyInsurance && (
                      <Button
                        onClick={() => onVerifyInsurance(selectedSession)}
                        style={{
                          backgroundColor: "#064260",
                          border: "none",
                          fontSize: "13px",
                          fontWeight: 600,
                          padding: "8px 16px",
                          borderRadius: "8px",
                        }}
                      >
                        Review Insurance
                      </Button>
                    )}
                </div>

                {verificationStatus === "Pending" && (
                  <div
                    style={{
                      backgroundColor: "#FFFBEB",
                      border: "1px solid #FDE68A",
                      padding: "16px",
                      borderRadius: "6px",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        display: "block",
                        color: "#92400E",
                      }}
                    >
                      Pending
                    </label>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "#92400E",
                        fontWeight: "400",
                      }}
                    >
                      Insurance verification is pending. Confirm coverage before
                      the session.
                    </p>
                  </div>
                )}

                {verificationStatus === "Verified" && (
                  <div>
                    <div
                      style={{
                        backgroundColor: "#F0FDF4",
                        border: "1px solid #BBF7D0",
                        padding: "16px",
                        borderRadius: "8px",
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ flex: "1.4", minWidth: "160px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: "#15803D",
                          }}
                        >
                          Active Coverage
                        </label>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#166534",
                            fontWeight: "500",
                          }}
                        >
                          {(() => {
                            const start = paymentData?.coverageStartDate
                              ? formatDate(
                                  paymentData.coverageStartDate,
                                  "date",
                                )
                              : null;
                            const end = paymentData?.coverageEndDate
                              ? formatDate(paymentData.coverageEndDate, "date")
                              : null;
                            if (start && end) return `${start} - ${end}`;
                            if (start) return start;
                            return "-";
                          })()}
                        </p>
                      </div>
                      <div style={{ flex: "0.8", minWidth: "90px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: "#15803D",
                          }}
                        >
                          Deductible
                        </label>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#166534",
                            fontWeight: "500",
                          }}
                        >
                          {paymentData?.hasRemainingDeductible ? "Yes" : "No"}
                        </p>
                      </div>
                      <div style={{ flex: "0.8", minWidth: "80px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: "#15803D",
                          }}
                        >
                          Copay
                        </label>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#166534",
                            fontWeight: "500",
                          }}
                        >
                          {paymentData?.copay
                            ? `$${String(paymentData.copay).replace(/^\$/, "")}`
                            : "-"}
                        </p>
                      </div>
                      <div style={{ flex: "0.8", minWidth: "90px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            display: "block",
                            color: "#15803D",
                          }}
                        >
                          Coinsurance
                        </label>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#166534",
                            fontWeight: "500",
                          }}
                        >
                          {paymentData?.coinsurance
                            ? `${String(paymentData.coinsurance).replace(/%$/, "")}%`
                            : "-"}
                        </p>
                      </div>
                    </div>
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "12px",
                        color: "#6B7280",
                      }}
                    >
                      Last Verified:{" "}
                      {paymentData?.lastVerifiedAt
                        ? formatDate(paymentData.lastVerifiedAt, "date")
                        : "-"}
                    </p>
                  </div>
                )}

                {verificationStatus === "Invalid" && (
                  <div>
                    <div
                      style={{
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FECACA",
                        padding: "16px",
                        borderRadius: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "6px",
                          display: "block",
                          color: "#B91C1C",
                        }}
                      >
                        Invalid
                      </label>
                      <p
                        style={{
                          margin: "0 0 10px 0",
                          fontSize: "13px",
                          color: "#991B1B",
                          fontWeight: "400",
                        }}
                      >
                        Your insurance is invalid.
                      </p>

                      {paymentData?.issueType && (
                        <div style={{ marginBottom: "10px" }}>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              marginBottom: "2px",
                              display: "block",
                              color: "#991B1B",
                            }}
                          >
                            Issue Type:
                          </label>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              color: "#7F1D1D",
                              fontWeight: "500",
                            }}
                          >
                            {paymentData.issueType}
                          </p>
                        </div>
                      )}

                      <p
                        style={{
                          margin: paymentData?.patientNotified !== undefined
                            ? "0 0 10px 0"
                            : 0,
                          fontSize: "13px",
                          color: "#991B1B",
                          fontWeight: "400",
                          lineHeight: 1.45,
                        }}
                      >
                        The patient has been notified to update insurance or
                        choose another payment method.
                      </p>

                      {paymentData?.patientNotified !== undefined &&
                        paymentData?.patientNotified !== null && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "12px",
                              color: "#7F1D1D",
                              fontWeight: "500",
                            }}
                          >
                            Patient notified:{" "}
                            {paymentData.patientNotified ? "Yes" : "No"}
                          </p>
                        )}
                    </div>
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "12px",
                        color: "#6B7280",
                      }}
                    >
                      Last Reviewed:{" "}
                      {paymentData?.lastVerifiedAt
                        ? formatDate(paymentData.lastVerifiedAt, "date")
                        : "-"}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <h4
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#064260",
                  }}
                >
                  Billing Info
                </h4>
                <div
                  style={{
                    backgroundColor: "#F0FDF4",
                    padding: "16px",
                    borderRadius: "6px",
                    display: "flex",
                    gap: "24px",
                    flexWrap: "nowrap",
                  }}
                >
                  <div style={{ flex: "1", minWidth: "200px" }}>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        display: "block",
                        color: "#15803D",
                      }}
                    >
                      Active Credit Card
                    </label>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "12px",
                        color: "#6B7280",
                        fontWeight: "400",
                      }}
                    >
                      Card on file and preauthorized.
                    </p>
                  </div>
                  <div style={{ flex: "1", minWidth: "200px" }}>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        display: "block",
                        color: "#15803D",
                      }}
                    >
                      Card on File
                    </label>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "14px",
                        color: "#4B5563",
                        fontWeight: "400",
                      }}
                    >
                      {paymentData?.cardLast4
                        ? `Visa • *${paymentData.cardLast4}`
                        : "N/A"}
                    </p>
                    {paymentData?.cardExpYear && paymentData?.cardExpMonth && (
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "12px",
                          color: "#6B7280",
                          fontWeight: "400",
                        }}
                      >
                        Exp: {String(paymentData.cardExpMonth).padStart(2, "0")}
                        /{String(paymentData.cardExpYear).slice(-2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {(selectedSession?.isCancelled ||
            selectedSession?.status === "System Cancel" ||
            selectedSession?.status === "Canceled by Super Admin" ||
            selectedSession?.status === "Canceled by Provider" ||
            selectedSession?.status === "Cancelled" ||
            selectedSession?.status === "canceled_by_user" ||
            selectedSession?.status === "late_cancelled") &&
            selectedSession?.cancellationReason && (
              <div
                style={{
                  marginBottom: "24px",
                  backgroundColor: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  borderRadius: "8px",
                  padding: "16px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <h4
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "12px",
                    color: "#DC2626",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>⚠️</span>
                  <span>Session Cancelled</span>
                </h4>
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px solid #FECACA",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        display: "block",
                        color: "#991B1B",
                      }}
                    >
                      Status:
                    </label>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "14px",
                        color: "#DC2626",
                        fontWeight: "600",
                      }}
                    >
                      {selectedSession?.status || "Cancelled"}
                    </p>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        marginBottom: "4px",
                        display: "block",
                        color: "#991B1B",
                      }}
                    >
                      Cancellation Reason:
                    </label>
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "14px",
                        color: "#1F2937",
                        lineHeight: "1.5",
                      }}
                    >
                      {selectedSession?.cancellationReason || "N/A"}
                    </p>
                  </div>
                  {selectedSession?.cancellationMessage && (
                    <div style={{ marginTop: "12px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          marginBottom: "4px",
                          display: "block",
                          color: "#991B1B",
                        }}
                      >
                        Additional Message:
                      </label>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          color: "#1F2937",
                          lineHeight: "1.5",
                          fontStyle: "italic",
                        }}
                      >
                        {selectedSession?.cancellationMessage}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          <div
            style={{
              marginBottom: "24px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "12px",
                color: "#1F2937",
              }}
            >
              Notes / Activity
            </h4>

            {loadingNotes ? (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                Loading notes...
              </div>
            ) : sessionNotes.length > 0 ? (
              <div
                style={{
                  marginBottom: "16px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  padding: "12px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "6px",
                  border: "1px solid #E5E7EB",
                }}
              >
                {sessionNotes.map((note) => (
                  <div
                    key={note._id || note.id}
                    style={{
                      marginBottom: "12px",
                      paddingBottom: "12px",
                      borderBottom:
                        sessionNotes.indexOf(note) < sessionNotes.length - 1
                          ? "1px solid #E5E7EB"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "4px",
                      }}
                    >
                      [{formatDate(note.createdAt, "date")},{" "}
                      {formatDate(note.createdAt, "time")}] —{" "}
                      {note.adminName || "Admin"}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#1F2937",
                        lineHeight: "1.5",
                      }}
                    >
                      {note.noteText}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <textarea
              placeholder="Add internal note"
              className="notes-textarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              style={{
                width: "100%",
                minHeight: "120px",
                padding: "12px",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                backgroundColor: "#FFFFFF",
                color: "#1F2937",
              }}
            />
            <style>
              {`
                .notes-textarea::placeholder {
                  color: #9CA3AF;
                }
              `}
            </style>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                marginTop: "12px",
                width: "100%",
              }}
            >
              <Button
                onClick={handleSaveNote}
                disabled={savingNote || !noteText.trim()}
                style={{
                  backgroundColor:
                    savingNote || !noteText.trim() ? "#9CA3AF" : "#27B99A",
                  border: "none",
                  color: "#ffffff",
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: "500",
                  borderRadius: "6px",
                  cursor:
                    savingNote || !noteText.trim() ? "not-allowed" : "pointer",
                  opacity: savingNote || !noteText.trim() ? 0.6 : 1,
                }}
              >
                {savingNote ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      show={showChangePaymentModal}
      onHide={() => !changingPaymentMethod && setShowChangePaymentModal(false)}
      centered
      backdrop="static"
      className="change-payment-method-modal-root"
      backdropClassName="change-payment-method-modal-backdrop"
    >
      <style>{`
        .change-payment-method-modal-root { z-index: 10050 !important; }
        .change-payment-method-modal-backdrop { z-index: 10049 !important; }
      `}</style>
      <Modal.Header closeButton style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Modal.Title style={{ fontSize: "18px", fontWeight: 600 }}>
          Change Payment Method
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "12px" }}>
          Session status will stay the same. Payment verification may reset
          based on the selected method.
        </p>
        <Form.Group>
          <Form.Label style={{ fontSize: "13px", fontWeight: 500 }}>
            Payment Method
          </Form.Label>
          <Form.Select
            value={selectedPaymentMethod}
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            disabled={changingPaymentMethod}
          >
            {PAYMENT_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #E5E7EB" }}>
        <Button
          variant="light"
          onClick={() => setShowChangePaymentModal(false)}
          disabled={changingPaymentMethod}
          style={{ border: "1px solid #D1D5DB" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleChangePaymentMethod}
          disabled={changingPaymentMethod}
          style={{ backgroundColor: "#064260", border: "none" }}
        >
          {changingPaymentMethod ? "Updating..." : "Update"}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default SessionDetailsDrawer;
